/* assets/glg-surveying-spatial-data.js — GLG-SURVEYING-SPATIAL-DATA-RUNTIME-14
 * Scope: surveying-spatial family collections.
 * Canonical interaction runtime for server-rendered surveying scenario/workflow markup.
 * Owns scenario disclosure, workflow classification/filtering, progressive reveal and
 * Enterprise mobile presentation helpers. It reuses server-rendered review routes and
 * does not rewrite server-rendered titles, summaries, prices or product contracts.
 */
(function (window, document) {
  'use strict';

  // Digital, Green and Water/Marine opt in without importing the Surveying visual skin.
  var PAGE_SELECTOR =
    '[data-collection-hero][data-collection-family="surveying-spatial"], [data-collection-hero][data-collection-handle="digital-city-geospatial-analytics"][data-collection-family="digital-intelligence"], [data-collection-hero][data-collection-handle="ecology-environmental-assessment"][data-collection-family="land-environment"], [data-collection-hero][data-collection-handle="hydrographic-marine-surveying"][data-collection-family="water-resilience"], [data-collection-hero][data-collection-handle="water-infrastructure-resilience"][data-collection-family="water-resilience"]';
  var SCENARIO_ROOT = '[data-collection-scenario-workspace]';
  var WORKFLOW_ROOT = '#collection-workflows';

  // Collection-only presentation grouping. Product URLs and commercial service lanes remain authoritative in Liquid.
  var INFRASTRUCTURE_CATEGORY_BY_HANDLE = Object.freeze({
    '3d-land-visualization-generator': 'spatial3d',
    'bathymetric-marine-survey': 'survey',
    'gps-control-network-surveying': 'survey',
    'structural-deformation-monitoring': 'survey',
    'cadastral-Note-surveying': 'survey',
    'survey-quality-inspection': 'survey',
    'ai-quality-inspector-system': 'ai',
    'engineering-submission-property-registration-consulting': 'ai',
    '3d-visualization-management-platform': 'spatial3d',
    'urban-renewal-feasibility': 'ai',
    'underground-utility-detection-modeling': 'survey',
    'uav-oblique-3d-modeling': 'survey',
    'satellite-remote-sensing-analysis': 'spatial3d',
    'gis-format-coordinate-conversion': 'spatial3d',
    'multi-layer-map-analysis': 'spatial3d',
    'surrounding-3d-map-service': 'spatial3d',
    'planning-submission-prep-assistant': 'ai',
    'topographic-survey-surface-analysis': 'survey',
    'ai-architecture-concept-design': 'ai',
    '3d-point-cloud-building-modeling': 'spatial3d',
  });

  // UR04: Collection-specific display categories; Shopify Product URLs remain authoritative.
  var URBAN_RENEWAL_CATEGORY_BY_HANDLE = Object.freeze({
    'urban-renewal-map-service': 'survey',
    'collective-land-circulation-advisory': 'survey',
    'land-circulation-document-preparation': 'survey',
    'collective-land-transfer-consulting': 'survey',
    'state-owned-land-transfer-consulting': 'survey',
    'land-transfer-advisory-service': 'survey',
    'property-rights-cancellation-service': 'survey',
    'registration-agency-service': 'survey',
    'housing-regularization-service': 'survey',
    'historical-land-compliance-consulting': 'survey',
    'public-intention-consultation-service': 'survey',
    'real-estate-integrated-consulting-service': 'survey',
    'urban-renewal-policy-research': 'spatial3d',
    'urban-village-redevelopment-assessment': 'spatial3d',
    'economic-balance-analysis': 'spatial3d',
    'demolition-compensation-cost-assessment': 'spatial3d',
    'urban-renewal-feasibility': 'spatial3d',
    'urban-renewal-policy-consulting': 'spatial3d',
    'idle-land-activation-planning': 'spatial3d',
    'policy-optimization-advisory': 'spatial3d',
    'urban-renewal-policy-solution-service': 'spatial3d',
    'point-land-supply-consulting': 'spatial3d',
    'community-infrastructure-upgrade': 'spatial3d',
    'industrial-property-division': 'spatial3d',
    'land-application-approval-service': 'ai',
    'land-preliminary-review-service': 'ai',
    'higher-level-policy-implementation-consulting': 'ai',
    'incentive-subsidy-application-service': 'ai',
    'enterprise-qualification-certification-service': 'ai',
    'housing-online-signing-registration': 'ai',
    'commercial-housing-sales-record-filing': 'ai',
    'subdivision-transfer-registration-service': 'ai',
    'subdivision-transfer-planning-preparation': 'ai',
    'industrial-property-subdivision-transfer': 'ai',
    'end-to-end-technical-coordination': 'ai',
    'planning-submission-service': 'ai',
    'project-initiation-consulting': 'ai',
    'post-approval-land-supply-service': 'ai',
    'engineering-submission-property-registration-consulting': 'ai',
    'post-approval-land-supply': 'ai',
  });

  function localeFamily() {
    var path = String((window.location && window.location.pathname) || '').toLowerCase();
    if (/\/(zh-cn|zh-hans|zh-sg)(\/|$)/.test(path)) return 'zh-CN';
    if (/\/(zh-hk|zh-tw|zh-hant)(\/|$)/.test(path)) return 'zh-HK';
    var lang = String(document.documentElement.lang || '').toLowerCase();
    if (lang === 'zh-cn' || lang.indexOf('zh-hans') === 0 || lang === 'zh-sg') return 'zh-CN';
    if (lang.indexOf('zh') === 0) return 'zh-HK';
    return 'en';
  }

  function labels() {
    var family = localeFamily();
    var page = document.querySelector(PAGE_SELECTOR);
    var infrastructure =
      page && page.getAttribute('data-collection-handle') === 'surveying-geomatics';
    var urban = page && page.getAttribute('data-collection-handle') === 'urban-renewal';
    var digital =
      page && page.getAttribute('data-collection-handle') === 'digital-city-geospatial-analytics';
    var green =
      page && page.getAttribute('data-collection-handle') === 'ecology-environmental-assessment';
    var marine =
      page &&
      (page.getAttribute('data-collection-handle') === 'hydrographic-marine-surveying' ||
        page.getAttribute('data-collection-handle') === 'water-infrastructure-resilience');
    if (marine) {
      if (family === 'zh-CN')
        return {
          expand: '查看详情',
          collapse: '收起详情',
          filters: '筛选水务海洋服务',
          all: '全部',
          survey: '水下地形与测深',
          spatial3d: '岸线与治理',
          ai: '监测与风险',
          results: '项',
          showing: '显示',
          showAll: '显示全部服务',
          showLess: '收起服务',
          review: '提交水务海洋需求',
          heroReview: '提交水务海洋需求',
          sticky: '需要专业支持？',
        };
      if (family === 'zh-HK')
        return {
          expand: '查看詳情',
          collapse: '收起詳情',
          filters: '篩選水務海洋服務',
          all: '全部',
          survey: '水下地形與測深',
          spatial3d: '岸線與治理',
          ai: '監測與風險',
          results: '項',
          showing: '顯示',
          showAll: '顯示全部服務',
          showLess: '收起服務',
          review: '提交水務海洋需求',
          heroReview: '提交水務海洋需求',
          sticky: '需要專業支援？',
        };
      return {
        expand: 'View details',
        collapse: 'Hide details',
        filters: 'Filter water and marine services',
        all: 'All',
        survey: 'Bathymetry & Terrain',
        spatial3d: 'Shoreline & Governance',
        ai: 'Monitoring & Risk',
        results: 'items',
        showing: 'Showing',
        showAll: 'Show all services',
        showLess: 'Show fewer services',
        review: 'Submit Water & Marine Brief',
        heroReview: 'Submit Water & Marine Brief',
        sticky: 'Need professional support?',
      };
    }
    if (green) {
      if (family === 'zh-CN')
        return {
          expand: '查看详情',
          collapse: '收起详情',
          filters: '筛选生态环境服务',
          all: '全部',
          survey: '本底与调查',
          spatial3d: '监测与治理',
          ai: '能源与 ESG',
          results: '项',
          showing: '显示',
          showAll: '显示全部服务',
          showLess: '收起服务',
          review: '提交环境项目需求',
          heroReview: '提交环境项目需求',
          sticky: '需要专业支持？',
        };
      if (family === 'zh-HK')
        return {
          expand: '查看詳情',
          collapse: '收起詳情',
          filters: '篩選生態環境服務',
          all: '全部',
          survey: '本底與調查',
          spatial3d: '監測與治理',
          ai: '能源與 ESG',
          results: '項',
          showing: '顯示',
          showAll: '顯示全部服務',
          showLess: '收起服務',
          review: '提交環境項目需求',
          heroReview: '提交環境項目需求',
          sticky: '需要專業支援？',
        };
      return {
        expand: 'View details',
        collapse: 'Hide details',
        filters: 'Filter environment services',
        all: 'All',
        survey: 'Baseline & Studies',
        spatial3d: 'Monitoring & Governance',
        ai: 'Energy & ESG',
        results: 'items',
        showing: 'Showing',
        showAll: 'Show all services',
        showLess: 'Show fewer services',
        review: 'Submit Environment Brief',
        heroReview: 'Submit Environment Brief',
        sticky: 'Need professional support?',
      };
    }
    if (digital) {
      if (family === 'zh-CN')
        return {
          expand: '查看详情',
          collapse: '收起详情',
          filters: '筛选数字城市服务',
          all: '全部',
          survey: '数据与 GIS',
          spatial3d: '三维与数字孪生',
          ai: '运营与工具',
          results: '项',
          showing: '显示',
          showAll: '显示全部服务',
          showLess: '收起服务',
          review: '提交数字城市需求',
          heroReview: '提交数字城市需求',
          sticky: '需要专业支持？',
        };
      if (family === 'zh-HK')
        return {
          expand: '查看詳情',
          collapse: '收起詳情',
          filters: '篩選數字城市服務',
          all: '全部',
          survey: '資料與 GIS',
          spatial3d: '三維與數字孿生',
          ai: '營運與工具',
          results: '項',
          showing: '顯示',
          showAll: '顯示全部服務',
          showLess: '收起服務',
          review: '提交數字城市需求',
          heroReview: '提交數字城市需求',
          sticky: '需要專業支援？',
        };
      return {
        expand: 'View details',
        collapse: 'Hide details',
        filters: 'Filter digital-city services',
        all: 'All',
        survey: 'Data & GIS',
        spatial3d: '3D & Digital Twins',
        ai: 'Operations & Tools',
        results: 'items',
        showing: 'Showing',
        showAll: 'Show all services',
        showLess: 'Show fewer services',
        review: 'Submit Digital City Brief',
        heroReview: 'Submit Digital City Brief',
        sticky: 'Need professional support?',
      };
    }
    if (urban) {
      if (family === 'zh-CN')
        return {
          expand: '查看详情',
          collapse: '收起详情',
          filters: '筛选城市更新服务',
          all: '全部',
          survey: '现状与权属',
          spatial3d: '价值与策略',
          ai: '报批与实施',
          results: '项',
          showing: '显示',
          showAll: '显示全部服务',
          showLess: '收起服务',
          review: '提交更新需求',
          heroReview: '提交更新需求',
          sticky: '需要专业复核？',
        };
      if (family === 'zh-HK')
        return {
          expand: '查看詳情',
          collapse: '收起詳情',
          filters: '篩選城市更新服務',
          all: '全部',
          survey: '現狀與權屬',
          spatial3d: '價值與策略',
          ai: '報批與實施',
          results: '項',
          showing: '顯示',
          showAll: '顯示全部服務',
          showLess: '收起服務',
          review: '提交更新需求',
          heroReview: '提交更新需求',
          sticky: '需要專業覆核？',
        };
      return {
        expand: 'View details',
        collapse: 'Hide details',
        filters: 'Filter renewal services',
        all: 'All',
        survey: 'Site & Rights',
        spatial3d: 'Value & Strategy',
        ai: 'Approvals & Delivery',
        results: 'items',
        showing: 'Showing',
        showAll: 'Show all services',
        showLess: 'Show fewer services',
        review: 'Submit Renewal Brief',
        heroReview: 'Submit Renewal Brief',
        sticky: 'Need professional review?',
      };
    }
    if (family === 'zh-CN')
      return {
        expand: '查看详情',
        collapse: '收起详情',
        filters: '筛选工作流',
        all: '全部',
        ai: infrastructure ? '规划与延伸工具' : 'AI 工具',
        survey: infrastructure ? '核心测绘服务' : '测绘服务',
        spatial3d: infrastructure ? 'GIS 与三维' : '3D 平台',
        results: '项',
        showing: '显示',
        showAll: '显示全部工作流',
        showLess: '收起工作流',
        review: '准备专业复核',
        heroReview: '提交需求复核',
        sticky: '需要专业复核？',
      };
    if (family === 'zh-HK')
      return {
        expand: '查看詳情',
        collapse: '收起詳情',
        filters: '篩選工作流',
        all: '全部',
        ai: infrastructure ? '規劃與延伸工具' : 'AI 工具',
        survey: infrastructure ? '核心測繪服務' : '測繪服務',
        spatial3d: infrastructure ? 'GIS 與三維' : '3D 平台',
        results: '項',
        showing: '顯示',
        showAll: '顯示全部工作流',
        showLess: '收起工作流',
        review: '準備專業覆核',
        heroReview: '提交需求覆核',
        sticky: '需要專業覆核？',
      };
    return {
      expand: 'View details',
      collapse: 'Hide details',
      filters: 'Filter workflows',
      all: 'All',
      ai: infrastructure ? 'Planning & Tools' : 'AI Tools',
      survey: infrastructure ? 'Core Surveying' : 'Surveying Services',
      spatial3d: infrastructure ? 'GIS & 3D' : '3D Platforms',
      results: 'items',
      showing: 'Showing',
      showAll: 'Show all workflows',
      showLess: 'Show fewer workflows',
      review: 'Prepare Review Brief',
      heroReview: 'Submit Engineering Review',
      sticky: 'Need professional review?',
    };
  }

  function setScenarioState(item, open, copy) {
    var button = item.querySelector('[data-survey-scenario-toggle]');
    var details = item.querySelector('[data-survey-scenario-details]');
    if (!button || !details) return;
    item.classList.toggle('is-open', open);
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    var label = button.querySelector('[data-survey-toggle-label]');
    if (label) label.textContent = open ? copy.collapse : copy.expand;
    // S05: keep the disclosure glyph in sync with the actual accessible state.
    var icon = button.querySelector('.survey-scenario-toggle__icon');
    if (icon) icon.textContent = open ? '−' : '+';
    details.hidden = !open;
  }

  function enhanceScenarios() {
    var copy = labels();
    Array.prototype.forEach.call(document.querySelectorAll(SCENARIO_ROOT), function (root) {
      if (root.dataset.surveyExpandableReady === 'true') return;
      var items = Array.prototype.slice.call(
        root.querySelectorAll('.mcpg__item[data-survey-scenario-card]')
      );

      items.forEach(function (item) {
        var button = item.querySelector('[data-survey-scenario-toggle]');
        var details = item.querySelector('[data-survey-scenario-details]');
        if (!button || !details || button.dataset.surveyBound === 'true') return;
        button.dataset.surveyBound = 'true';
        setScenarioState(item, false, copy);
        button.addEventListener('click', function () {
          var willOpen = button.getAttribute('aria-expanded') !== 'true';
          items.forEach(function (other) {
            if (other !== item) setScenarioState(other, false, copy);
          });
          setScenarioState(item, willOpen, copy);
        });
      });

      root.dataset.surveyExpandableReady = 'true';
      root.classList.add('is-surveying-enhanced');
    });
  }

  function enhanceScenarioCarousel(page) {
    var carouselHandle = page.getAttribute('data-collection-handle');
    if (
      carouselHandle !== 'surveying-geomatics' &&
      carouselHandle !== 'urban-renewal' &&
      carouselHandle !== 'property-asset-valuation' &&
      carouselHandle !== 'digital-city-geospatial-analytics' &&
      carouselHandle !== 'ecology-environmental-assessment' &&
      carouselHandle !== 'hydrographic-marine-surveying' &&
      carouselHandle !== 'water-infrastructure-resilience'
    )
      return;
    var roots = document.querySelectorAll(SCENARIO_ROOT);
    if (!roots.length) return;
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.surveyCarouselReady === 'true') return;
      var track = root.querySelector('[data-collection-scenario-grid]');
      var controls = root.querySelector('[data-survey-carousel-controls]');
      if (!track || !controls) return;
      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-survey-scenario-card]'));
      var previous = controls.querySelector('[data-survey-carousel-prev]');
      var next = controls.querySelector('[data-survey-carousel-next]');
      var status = controls.querySelector('[data-survey-carousel-status]');
      if (!cards.length || !previous || !next || !status) return;

      var scrollFrame = 0;
      var currentIndex = 0;
      function syncCarousel() {
        scrollFrame = 0;
        var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        var hasOverflow = maxScroll > 2;
        controls.hidden = !hasOverflow;
        if (!hasOverflow) {
          currentIndex = 0;
          previous.disabled = true;
          next.disabled = true;
          status.textContent = '1 / ' + cards.length;
          root.setAttribute('data-survey-carousel-index', '1');
          return;
        }
        var left = track.getBoundingClientRect().left;
        var nearestDistance = Infinity;
        cards.forEach(function (card, index) {
          var distance = Math.abs(card.getBoundingClientRect().left - left);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            currentIndex = index;
          }
        });
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft >= maxScroll - 2;
        if (atEnd) currentIndex = cards.length - 1;
        previous.disabled =
          atStart ||
          ((carouselHandle === 'urban-renewal' ||
            carouselHandle === 'property-asset-valuation' ||
            carouselHandle === 'digital-city-geospatial-analytics' ||
            carouselHandle === 'ecology-environmental-assessment' ||
            carouselHandle === 'hydrographic-marine-surveying' ||
            carouselHandle === 'water-infrastructure-resilience') &&
            currentIndex === 0);
        next.disabled = atEnd;
        var message = currentIndex + 1 + ' / ' + cards.length;
        if (status.textContent !== message) status.textContent = message;
        root.setAttribute('data-survey-carousel-index', String(currentIndex + 1));
      }
      function scheduleCarouselSync() {
        if (scrollFrame || !window.requestAnimationFrame) {
          if (!window.requestAnimationFrame) syncCarousel();
          return;
        }
        scrollFrame = window.requestAnimationFrame(syncCarousel);
      }
      function moveCarousel(step) {
        if (controls.hidden || !step) return;
        var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        var targetIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + step));
        var firstLeft = cards[0].getBoundingClientRect().left;
        var targetLeft = cards[targetIndex].getBoundingClientRect().left - firstLeft;
        targetLeft = Math.max(0, Math.min(maxScroll, targetLeft));

        // S03: the penultimate snap can be beyond maxScroll on wide screens.
        // When its clamped target is already at the edge, move one visible card stride.
        if (Math.abs(targetLeft - track.scrollLeft) <= 2) {
          var stride =
            cards.length > 1
              ? Math.abs(cards[1].getBoundingClientRect().left - firstLeft)
              : track.clientWidth;
          targetLeft = Math.max(
            0,
            Math.min(maxScroll, track.scrollLeft + (step < 0 ? -stride : stride))
          );
        }
        var reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({ left: targetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      previous.addEventListener('click', function () {
        moveCarousel(-1);
      });
      next.addEventListener('click', function () {
        moveCarousel(1);
      });
      controls.addEventListener('keydown', function (event) {
        if (event.target !== previous && event.target !== next) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        var step = 0;
        if (event.key === 'ArrowLeft') step = -1;
        else if (event.key === 'ArrowRight') step = 1;
        else if (event.key === 'Home') step = -currentIndex;
        else if (event.key === 'End') step = cards.length - 1 - currentIndex;
        else return;
        event.preventDefault();
        moveCarousel(step);
      });
      track.addEventListener('scroll', scheduleCarouselSync, { passive: true });
      root.__glgSurveyCarouselRefresh = function () {
        if (root.isConnected) scheduleCarouselSync();
      };
      root.dataset.surveyCarouselReady = 'true';
      scheduleCarouselSync();
    });
    window.__GLG_SURVEY_CAROUSEL_RESIZE_REFRESH__ = function () {
      Array.prototype.forEach.call(document.querySelectorAll(SCENARIO_ROOT), function (root) {
        if (typeof root.__glgSurveyCarouselRefresh === 'function')
          root.__glgSurveyCarouselRefresh();
      });
    };
  }

  function normalizeCategory(value) {
    var clean = String(value || '')
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .trim();
    if (clean === 'ai' || clean === 'ai-tool' || clean === 'ai-tools' || clean === 'tool')
      return 'ai';
    if (
      clean === 'survey' ||
      clean === 'surveying' ||
      clean === 'surveying-service' ||
      clean === 'surveying-services' ||
      clean === 'service'
    )
      return 'survey';
    if (
      clean === '3d' ||
      clean === 'spatial3d' ||
      clean === '3d-platform' ||
      clean === '3d-platforms' ||
      clean === 'platform'
    )
      return 'spatial3d';
    return '';
  }

  function classifyWorkflow(item) {
    var declared = normalizeCategory(item.getAttribute('data-survey-category'));
    var cardType = String(item.getAttribute('data-card-type') || '').toLowerCase();
    var delivery = String(item.getAttribute('data-delivery-surface') || '').toLowerCase();
    var handle = String(item.getAttribute('data-survey-product-handle') || '').toLowerCase();
    var category = declared;
    var source = declared ? 'declared' : '';
    var page = document.querySelector(PAGE_SELECTOR);
    var isInfrastructure =
      page && page.getAttribute('data-collection-handle') === 'surveying-geomatics';
    var isUrbanParity = page && page.getAttribute('data-collection-handle') === 'urban-renewal';
    if (
      isUrbanParity &&
      Object.prototype.hasOwnProperty.call(URBAN_RENEWAL_CATEGORY_BY_HANDLE, handle)
    ) {
      category = URBAN_RENEWAL_CATEGORY_BY_HANDLE[handle];
      source = 'curated-urban-handle';
    }
    if (
      !category &&
      isInfrastructure &&
      Object.prototype.hasOwnProperty.call(INFRASTRUCTURE_CATEGORY_BY_HANDLE, handle)
    ) {
      category = INFRASTRUCTURE_CATEGORY_BY_HANDLE[handle];
      source = 'curated-product-handle';
    }

    if (!category) {
      if (delivery === 'tool' || cardType === 'tool' || cardType.indexOf('ai') !== -1) {
        category = 'ai';
        source = 'delivery-contract';
      } else if (
        delivery === 'platform' ||
        cardType === 'platform' ||
        cardType.indexOf('platform') !== -1
      ) {
        category = 'spatial3d';
        source = 'delivery-contract';
      } else if (
        delivery === 'service' ||
        cardType === 'professional service' ||
        cardType.indexOf('service') !== -1
      ) {
        category = 'survey';
        source = 'delivery-contract';
      }
    }

    if (!category) {
      var isAi =
        /(^|[-_])ai($|[-_])|quality-inspector|tender-intelligence|architecture-concept|agent|intelligence/.test(
          handle
        );
      var isSpatial3d =
        /(^|[-_])3d($|[-_])|visualization|visualisation|digital-twin|point-cloud|gis|map-api|multi-layer|platform|modeling|modelling|bim|reality|reconstruction/.test(
          handle
        );
      category = isAi ? 'ai' : isSpatial3d ? 'spatial3d' : 'survey';
      source = 'legacy-handle-fallback';
    }

    item.setAttribute('data-survey-categories', category);
    item.setAttribute('data-survey-classification-source', source);
    return category;
  }

  function enhanceWorkflowDescriptions() {
    var root = document.querySelector(WORKFLOW_ROOT);
    if (!root || root.dataset.surveyDescriptionReady === 'true') return;
    var page = document.querySelector(PAGE_SELECTOR);
    if (
      page &&
      (page.getAttribute('data-collection-handle') === 'surveying-geomatics' ||
        page.getAttribute('data-collection-handle') === 'urban-renewal')
    )
      return;
    var copy = labels();
    var cards = Array.prototype.slice.call(
      root.querySelectorAll('.product-card[data-survey-workflow-card]')
    );

    cards.forEach(function (card, index) {
      var meta = card.querySelector('[data-survey-workflow-description]');
      if (!meta || card.querySelector('[data-survey-description-toggle]')) return;

      if (!meta.id) meta.id = 'survey-workflow-description-' + (index + 1);
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'survey-workflow-description-toggle';
      button.setAttribute('data-survey-description-toggle', '');
      button.setAttribute('aria-controls', meta.id);
      button.setAttribute('aria-expanded', 'false');
      button.textContent = copy.expand;
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        card.classList.toggle('is-description-expanded', expanded);
        button.textContent = expanded ? copy.collapse : copy.expand;
        scheduleWorkflowDescriptionSync();
      });
      meta.insertAdjacentElement('afterend', button);
    });

    root.dataset.surveyDescriptionReady = 'true';
  }

  function syncWorkflowDescriptionButtons() {
    var root = document.querySelector(WORKFLOW_ROOT);
    if (!(root instanceof HTMLElement)) return;
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-survey-workflow-card]'),
      function (card) {
        var item = card.closest('.mcpg__item');
        if (item && item.hidden) return;
        var description = card.querySelector('[data-survey-workflow-description]');
        var toggle = card.querySelector('[data-survey-description-toggle]');
        if (!description || !toggle) return;
        if (card.classList.contains('is-description-expanded')) {
          toggle.hidden = false;
          return;
        }
        toggle.hidden = description.scrollHeight <= description.clientHeight + 1;
      }
    );
  }

  function scheduleWorkflowDescriptionSync() {
    if (window.requestAnimationFrame) window.requestAnimationFrame(syncWorkflowDescriptionButtons);
    else syncWorkflowDescriptionButtons();
  }

  function enhanceWorkflowFilters() {
    const infrastructurePage = document.querySelector(
      '[data-collection-hero][data-collection-handle="surveying-geomatics"]'
    );
    if (infrastructurePage) return;

    var root = document.querySelector(WORKFLOW_ROOT);
    if (!root || root.dataset.surveyFilterReady === 'true') return;
    var filterPage = document.querySelector(PAGE_SELECTOR);
    if (
      filterPage &&
      filterPage.getAttribute('data-collection-handle') === 'property-asset-valuation'
    )
      return;
    var grid = root.querySelector('[data-collection-workflow-grid]');
    if (!grid) return;

    var items = Array.prototype.filter.call(grid.children, function (node) {
      return node.classList && node.classList.contains('mcpg__item');
    });
    if (!items.length) return;

    var copy = labels();
    items.forEach(function (item) {
      classifyWorkflow(item);
    });

    root.dataset.surveyCardsReady = 'true';
    root.classList.add('is-surveying-cards-enhanced');

    // A single verified marine product needs its full service card, not empty filter categories.
    if (
      filterPage &&
      filterPage.getAttribute('data-collection-handle') === 'hydrographic-marine-surveying' &&
      items.length < 2
    ) {
      root.dataset.surveyFilterReady = 'true';
      root.dataset.waterMarineSingleService = 'true';
      return;
    }

    var surveyPage = document.querySelector(PAGE_SELECTOR);
    var isInfrastructure =
      surveyPage && surveyPage.getAttribute('data-collection-handle') === 'surveying-geomatics';
    var isUrbanParity =
      surveyPage && surveyPage.getAttribute('data-collection-handle') === 'urban-renewal';
    var isDigitalParity =
      surveyPage &&
      surveyPage.getAttribute('data-collection-handle') === 'digital-city-geospatial-analytics';
    var isGreenParity =
      surveyPage &&
      surveyPage.getAttribute('data-collection-handle') === 'ecology-environmental-assessment';
    var activeKey = isInfrastructure ? 'survey' : 'all';
    if (isUrbanParity) activeKey = 'survey';
    var expandedAll = false;
    var mobileBreakpoint = isInfrastructure ? 767 : 749;
    if (isUrbanParity || isDigitalParity || isGreenParity) mobileBreakpoint = 767;
    function workflowInitialLimit() {
      return window.matchMedia &&
        window.matchMedia('(max-width: ' + mobileBreakpoint + 'px)').matches
        ? 4
        : 6;
    }
    var initialLimit = workflowInitialLimit();

    var filterBar = document.createElement('div');
    filterBar.className = 'survey-workflow-filter';
    filterBar.setAttribute('data-survey-workflow-filter', '');
    filterBar.setAttribute('role', 'group');
    filterBar.setAttribute('aria-label', copy.filters);

    var options =
      surveyPage &&
      (surveyPage.getAttribute('data-collection-handle') === 'surveying-geomatics' ||
        isUrbanParity ||
        isDigitalParity ||
        isGreenParity)
        ? [
            ['all', copy.all],
            ['survey', copy.survey],
            ['spatial3d', copy.spatial3d],
            ['ai', copy.ai],
          ]
        : [
            ['all', copy.all],
            ['ai', copy.ai],
            ['survey', copy.survey],
            ['spatial3d', copy.spatial3d],
          ];
    var count = document.createElement('span');
    count.className = 'survey-workflow-filter__count';
    count.setAttribute('aria-live', 'polite');

    var reveal = document.createElement('button');
    reveal.type = 'button';
    reveal.className = 'survey-workflow-reveal';
    reveal.setAttribute('data-survey-workflow-reveal', '');
    reveal.setAttribute('aria-expanded', 'false');
    if ((isInfrastructure || isUrbanParity || isDigitalParity || isGreenParity) && grid.id)
      reveal.setAttribute('aria-controls', grid.id);

    function categoryMatch(item, key) {
      return key === 'all' || item.getAttribute('data-survey-categories') === key;
    }

    function matchingItems(key) {
      return items.filter(function (item) {
        return categoryMatch(item, key);
      });
    }

    function anchoredWorkflowItem() {
      var fragment = String((window.location && window.location.hash) || '');
      if (fragment.charAt(0) !== '#') return null;
      var id;
      try {
        id = decodeURIComponent(fragment.slice(1));
      } catch (_error) {
        return null;
      }
      if (!id) return null;
      // Only match an actual card in this Collection grid, not an unrelated section.
      return (
        items.find(function (item) {
          if (item.id === id && item.getAttribute('data-glg-service-anchor') === id) return true;
          // A second fragment may alias the *same* visible workflow card.
          // Never change filter ownership or reveal a different Collection's item.
          var alternate =
            typeof item.querySelector === 'function'
              ? item.querySelector('[data-glg-service-anchor-alias]')
              : null;
          return Boolean(
            alternate &&
            alternate.id === id &&
            alternate.getAttribute('data-glg-service-anchor-alias') === id
          );
        }) || null
      );
    }

    function applyFilter(key) {
      activeKey = key;
      var matches = matchingItems(key);
      var limitActive = key === 'all' && !expandedAll && matches.length > initialLimit;
      var visibleCount = 0;

      items.forEach(function (item) {
        var match = categoryMatch(item, key);
        var index = match ? matches.indexOf(item) : -1;
        var visible = match && (!limitActive || index < initialLimit);
        item.hidden = !visible;
        item.toggleAttribute('data-survey-progressive-hidden', match && !visible);
        if (visible) visibleCount += 1;
      });

      Array.prototype.forEach.call(
        filterBar.querySelectorAll('button[data-filter-key]'),
        function (button) {
          button.setAttribute(
            'aria-pressed',
            button.getAttribute('data-filter-key') === key ? 'true' : 'false'
          );
        }
      );

      count.textContent =
        key === 'all' && !expandedAll && matches.length > initialLimit
          ? copy.showing + ' ' + visibleCount + ' / ' + matches.length + ' ' + copy.results
          : matches.length + ' ' + copy.results;

      if (key === 'all' && matches.length > initialLimit) {
        reveal.hidden = false;
        reveal.setAttribute('aria-expanded', expandedAll ? 'true' : 'false');
        reveal.textContent = expandedAll
          ? copy.showLess + ' ↑'
          : copy.showAll + ' (' + matches.length + ') ↓';
      } else {
        reveal.hidden = true;
      }

      root.setAttribute('data-active-survey-filter', key);
      root.setAttribute('data-survey-all-expanded', expandedAll ? 'true' : 'false');
      scheduleWorkflowDescriptionSync();
    }

    // This Section-specific callback reuses the existing single global resize listener.
    window.__GLG_SURVEY_FILTER_RESIZE_REFRESH__ = function () {
      if (!root.isConnected) return;
      var nextLimit = workflowInitialLimit();
      if (nextLimit === initialLimit) return;
      initialLimit = nextLimit;
      applyFilter(activeKey);
    };

    options.forEach(function (option) {
      var key = option[0];
      var matches = matchingItems(key).length;
      if (matches === 0 && key !== 'all') return;
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'survey-workflow-filter__button';
      button.setAttribute('data-filter-key', key);
      button.setAttribute('aria-pressed', key === activeKey ? 'true' : 'false');
      button.innerHTML =
        '<span class="survey-workflow-filter__label">' +
        option[1] +
        '</span>' +
        '<span class="survey-workflow-filter__badge" aria-hidden="true">(' +
        matches +
        ')</span>';
      button.addEventListener('click', function () {
        expandedAll = false;
        applyFilter(key);
      });
      filterBar.appendChild(button);
    });

    filterBar.appendChild(count);
    reveal.addEventListener('click', function () {
      if (activeKey !== 'all') return;
      expandedAll = !expandedAll;
      applyFilter('all');
      if (!expandedAll) {
        // W04a: return focus to the visible All filter before scrolling away from Show fewer.
        var allFilterButton = filterBar.querySelector('button[data-filter-key="all"]');
        if (allFilterButton) allFilterButton.focus({ preventScroll: true });
        const rootStyle = window.getComputedStyle(root);
        const parsedMargin = Number.parseFloat(rootStyle.scrollMarginTop);
        const scrollMargin = Number.isFinite(parsedMargin) ? Math.max(0, parsedMargin) : 20;
        const stickyHeader = document.querySelector(
          '.globalnav[data-shell-surface="context-bar"].header--sticky'
        );
        const headerBottom = stickyHeader
          ? Math.max(0, stickyHeader.getBoundingClientRect().bottom)
          : 0;
        const scrollOffset = Math.max(scrollMargin, headerBottom > 0 ? headerBottom + 16 : 0);
        const targetTop = Math.max(
          0,
          root.getBoundingClientRect().top + window.scrollY - scrollOffset
        );
        const reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: targetTop,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    });

    grid.before(filterBar);
    grid.after(reveal);

    root.dataset.surveyFilterReady = 'true';
    root.classList.add('is-surveying-filter-enhanced');
    applyFilter(activeKey);

    function revealFragmentService() {
      var target = anchoredWorkflowItem();
      if (!target) return;
      var category = target.getAttribute('data-survey-categories');
      if (!category) return;
      // The target must be visible even when the default category is 'survey'
      // or when an earlier 'all' view was progressively limited.
      expandedAll = false;
      applyFilter(category);
      function positionTarget() {
        if (!root.isConnected || target.hidden || anchoredWorkflowItem() !== target) return;
        if (typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      }
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(positionTarget);
      } else {
        positionTarget();
      }
    }

    var fragmentController = new AbortController();
    window.addEventListener('hashchange', revealFragmentService, {
      signal: fragmentController.signal,
    });
    window.addEventListener('popstate', revealFragmentService, {
      signal: fragmentController.signal,
    });
    document.addEventListener(
      'shopify:section:unload',
      function (event) {
        if (event.target && event.target.contains(root)) fragmentController.abort();
      },
      { signal: fragmentController.signal }
    );
    revealFragmentService();
  }

  function enhanceHeroReview(page) {
    if (page.getAttribute('data-collection-handle') === 'property-asset-valuation') {
      var staleAssetReview = page.querySelector(
        '.collection-hero-workspace__actions [data-survey-hero-review]'
      );
      if (staleAssetReview) staleAssetReview.remove();
      return;
    }
    // H03/H04: Urban Renewal and Surveying have one Liquid-owned Hero CTA; retire stale JS-added review links.
    if (
      (page.getAttribute('data-collection-handle') === 'urban-renewal' ||
        page.getAttribute('data-collection-handle') === 'surveying-geomatics') &&
      page.getAttribute('data-collection-family') === 'surveying-spatial'
    ) {
      var retiredReview = page.querySelector(
        '.collection-hero-workspace__actions [data-survey-hero-review]'
      );
      if (retiredReview) retiredReview.remove();
      return;
    }
    var actions = page.querySelector('.collection-hero-workspace__actions');
    if (!actions || actions.querySelector('.collection-hero-workspace__btn--secondary')) return;
    var reviewRoute = document.querySelector(
      '.gcity-rich-text[data-editorial-cta] .button--primary'
    );
    if (!reviewRoute || !reviewRoute.getAttribute('href')) return;

    var link = document.createElement('a');
    link.className = 'collection-hero-workspace__btn collection-hero-workspace__btn--secondary';
    link.setAttribute('data-survey-hero-review', '');
    link.href = reviewRoute.getAttribute('href');
    link.textContent = labels().heroReview;
    actions.appendChild(link);
  }

  function enhanceStickyLead(page) {
    if (
      page.getAttribute('data-collection-handle') === 'surveying-geomatics' ||
      page.getAttribute('data-collection-handle') === 'urban-renewal' ||
      page.getAttribute('data-collection-handle') === 'property-asset-valuation' ||
      page.getAttribute('data-collection-handle') === 'digital-city-geospatial-analytics' ||
      page.getAttribute('data-collection-handle') === 'ecology-environmental-assessment' ||
      page.getAttribute('data-collection-handle') === 'hydrographic-marine-surveying' ||
      page.getAttribute('data-collection-handle') === 'water-infrastructure-resilience'
    )
      return;
    if (document.querySelector('[data-survey-sticky-lead]')) return;
    var reviewRoute =
      page.querySelector('.collection-hero-workspace__btn--secondary') ||
      document.querySelector('.gcity-rich-text[data-editorial-cta] .button--primary');
    if (!reviewRoute || !reviewRoute.getAttribute('href')) return;

    var copy = labels();
    var bar = document.createElement('div');
    bar.className = 'survey-sticky-lead';
    bar.setAttribute('data-survey-sticky-lead', '');
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', copy.sticky);
    bar.hidden = true;

    var text = document.createElement('span');
    text.className = 'survey-sticky-lead__label';
    text.textContent = copy.sticky;

    var link = document.createElement('a');
    link.className = 'survey-sticky-lead__cta';
    link.href = reviewRoute.getAttribute('href');
    link.textContent = copy.heroReview;

    bar.appendChild(text);
    bar.appendChild(link);
    document.body.appendChild(bar);

    var footerCta = document.querySelector('.gcity-rich-text[data-editorial-cta]');
    if ('IntersectionObserver' in window) {
      var heroObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.hidden = entry.isIntersecting;
          });
        },
        { threshold: 0.08 }
      );
      heroObserver.observe(page);

      if (footerCta) {
        var footerObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) bar.hidden = true;
              else if (page.getBoundingClientRect().bottom < 0) bar.hidden = false;
            });
          },
          { threshold: 0.04 }
        );
        footerObserver.observe(footerCta);
      }
    }
  }

  function markRuntimeReady(page) {
    page.classList.add('is-surveying-runtime-ready');
    page.setAttribute('data-surveying-runtime-ready', 'true');
    document.documentElement.classList.add('has-surveying-runtime-ready');
  }

  function enhanceStepNavigation() {
    document
      .querySelectorAll(
        '[data-collection-scenario-workspace]' +
          '[data-source-collection-default="surveying-geomatics"] ' +
          '[data-survey-step-cards]'
      )
      .forEach((nav) => {
        if (nav.__surveyStepCleanup) nav.__surveyStepCleanup();

        const items = Array.from(nav.querySelectorAll('a[href^="#"]'))
          .map((link) => ({
            link,
            target: document.getElementById(link.hash.slice(1)),
          }))
          .filter((item) => item.target);
        if (!items.length) return;

        const controller = new AbortController();
        let frame = 0;

        function update() {
          frame = 0;
          if (controller.signal.aborted) return;

          const threshold = Math.min(180, window.innerHeight * 0.25);
          const ordered = items
            .slice()
            .sort(
              (a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top
            );
          let current = ordered[0];

          ordered.forEach((item) => {
            if (item.target.getBoundingClientRect().top <= threshold) {
              current = item;
            }
          });

          items.forEach(({ link }) => {
            if (link === current.link) {
              link.setAttribute('aria-current', 'step');
            } else {
              link.removeAttribute('aria-current');
            }
          });
        }

        function queueUpdate() {
          if (!frame) frame = window.requestAnimationFrame(update);
        }

        items.forEach(({ link, target }) => {
          link.addEventListener(
            'click',
            (event) => {
              if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              )
                return;

              event.preventDefault();
              const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

              target.scrollIntoView({
                behavior: reduced ? 'auto' : 'smooth',
                block: 'start',
              });

              if (window.location.hash !== link.hash) {
                window.history.pushState(null, '', link.hash);
              }
              queueUpdate();
            },
            { signal: controller.signal }
          );
        });

        function cleanup() {
          controller.abort();
          if (frame) window.cancelAnimationFrame(frame);
          frame = 0;
          items.forEach(({ link }) => {
            link.removeAttribute('aria-current');
          });
          delete nav.__surveyStepCleanup;
        }

        nav.__surveyStepCleanup = cleanup;
        window.addEventListener('scroll', queueUpdate, {
          passive: true,
          signal: controller.signal,
        });
        window.addEventListener('resize', queueUpdate, {
          passive: true,
          signal: controller.signal,
        });
        window.addEventListener('hashchange', queueUpdate, {
          signal: controller.signal,
        });
        window.addEventListener('pageshow', queueUpdate, {
          signal: controller.signal,
        });

        document.addEventListener(
          'shopify:section:unload',
          (event) => {
            if (
              event.target.contains(nav) ||
              items.some(({ target }) => event.target.contains(target))
            )
              cleanup();
          },
          { signal: controller.signal }
        );

        update();
      });
  }

  function boot() {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;

    const isInfrastructure = page.getAttribute('data-collection-handle') === 'surveying-geomatics';

    if (!isInfrastructure) {
      enhanceStepNavigation();
      enhanceScenarios();
      enhanceScenarioCarousel(page);
      enhanceWorkflowFilters();
      enhanceWorkflowDescriptions();
      scheduleWorkflowDescriptionSync();
      enhanceHeroReview(page);
      enhanceStickyLead(page);
    }

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        markRuntimeReady(page);
      });
    } else {
      markRuntimeReady(page);
    }
  }

  if (!window.__GLG_SURVEY_WORKFLOW_DESCRIPTION_RESIZE_BOUND__) {
    window.__GLG_SURVEY_WORKFLOW_DESCRIPTION_RESIZE_BOUND__ = true;
    var descriptionResizeFrame = 0;
    window.addEventListener(
      'resize',
      function () {
        if (descriptionResizeFrame || !window.requestAnimationFrame) return;
        descriptionResizeFrame = window.requestAnimationFrame(function () {
          descriptionResizeFrame = 0;
          if (typeof window.__GLG_SURVEY_FILTER_RESIZE_REFRESH__ === 'function') {
            window.__GLG_SURVEY_FILTER_RESIZE_REFRESH__();
          }
          if (typeof window.__GLG_SURVEY_CAROUSEL_RESIZE_REFRESH__ === 'function') {
            window.__GLG_SURVEY_CAROUSEL_RESIZE_REFRESH__();
          }
          syncWorkflowDescriptionButtons();
        });
      },
      { passive: true }
    );
  }

  // W04: Theme Editor may re-evaluate this asset; replace the old boot callback.
  // Per-node ready guards remain the single owner of scenario and workflow listeners.
  var previousBoot = window.__GLG_SURVEY_BOOT_LISTENER__;
  if (typeof previousBoot === 'function') {
    document.removeEventListener('DOMContentLoaded', previousBoot);
    document.removeEventListener('shopify:section:load', previousBoot);
  }
  window.__GLG_SURVEY_BOOT_LISTENER__ = boot;
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})(window, document);
